import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Edit3, Droplet, Flame, Plug } from 'lucide-react';

export default function UtilityDetailDialog({ 
  open, 
  onOpenChange, 
  utilityType, 
  utilityData, 
  onUpdate 
}) {
  const [editMode, setEditMode] = useState(false);
  const [selectMode, setSelectMode] = useState(!utilityType);
  const [selectedType, setSelectedType] = useState(utilityType);
  const [formData, setFormData] = useState(utilityData || {});

  // Re-sync internal state with props every time the dialog opens.
  // - Existing utility (utilityType set) → open in read-only VIEW mode (Edit + Close)
  // - Add utility (utilityType null) → open the type picker; once a type is picked
  //   we go straight into EDIT mode (no data to view yet).
  useEffect(() => {
    if (!open) return;
    if (utilityType) {
      setSelectedType(utilityType);
      setSelectMode(false);
      setEditMode(false);
    } else {
      setSelectedType(null);
      setSelectMode(true);
      setEditMode(false);
    }
    setFormData(utilityData || {});
  }, [open, utilityType, utilityData]);

  const utilityTypes = [
    {
      type: 'water',
      icon: Droplet,
      label: 'Water',
      color: '#48cae4'
    },
    {
      type: 'gas',
      icon: Flame,
      label: 'Gas',
      color: '#f59e0b'
    },
    {
      type: 'electric',
      icon: Plug,
      label: 'Electric',
      color: '#667eea'
    }
  ];

  const getUtilityConfig = (type) => {
    return utilityTypes.find(u => u.type === type) || utilityTypes[0];
  };

  const config = getUtilityConfig(selectedType || utilityType);
  const Icon = config.icon;
  // The actual utility key prefix to read/write on the property object.
  // When ADDING a new utility, `utilityType` (prop) is null but `selectedType`
  // (local state) is set once the user picks Water/Gas/Electric. We must use
  // this combined value everywhere the form binds to `${type}_meter` etc.,
  // otherwise the fields persist under bogus keys like `null_meter` and the
  // backend silently drops them. (Bug fix Feb 24, 2026.)
  const currentType = selectedType || utilityType;

  const handleSelectUtility = (type) => {
    setSelectedType(type);
    setSelectMode(false);
    setEditMode(true);
  };

  const handleSave = () => {
    onUpdate(selectedType || utilityType, formData);
    setEditMode(false);
    setSelectMode(false);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setFormData(utilityData || {});
    setEditMode(false);
    if (!utilityType) {
      setSelectMode(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      onOpenChange(isOpen);
      if (!isOpen) {
        setSelectMode(!utilityType);
        setEditMode(false);
      }
    }}>
      <DialogContent className="utility-detail-dialog">
        <DialogHeader>
          <div className="utility-dialog-header">
            {!selectMode && (
              <>
                <div className="utility-dialog-icon" style={{ background: config.color }}>
                  <Icon size={32} color="white" />
                </div>
                <DialogTitle>{config.label} {utilityType ? 'Details' : ''}</DialogTitle>
              </>
            )}
            {selectMode && (
              <DialogTitle>Select Utility Type</DialogTitle>
            )}
          </div>
          <DialogDescription>
            {selectMode
              ? 'Choose which utility you want to add: water, gas, or electric.'
              : 'Record meter serial number, supplier, start reading, and latest reading for this utility.'}
          </DialogDescription>
        </DialogHeader>

        {selectMode ? (
          <div className="utility-type-selector">
            {utilityTypes.map((util) => {
              const UtilIcon = util.icon;
              return (
                <div
                  key={util.type}
                  className="utility-type-option"
                  onClick={() => handleSelectUtility(util.type)}
                  style={{ borderColor: util.color }}
                >
                  <div className="utility-type-icon" style={{ background: util.color }}>
                    <UtilIcon size={28} color="white" />
                  </div>
                  <div className="utility-type-label">{util.label}</div>
                </div>
              );
            })}
          </div>
        ) : !editMode ? (
          <div className="utility-detail-view">
            <div className="utility-detail-grid">
              <div className="utility-detail-item">
                <div className="utility-detail-label">Serial Number</div>
                <div className="utility-detail-value">
                  {formData[`${currentType}_meter`] || 'Not set'}
                </div>
              </div>
              <div className="utility-detail-item">
                <div className="utility-detail-label">Utility Company</div>
                <div className="utility-detail-value">
                  {formData[`${currentType}_supplier`] || 'Not set'}
                </div>
              </div>
              <div className="utility-detail-item">
                <div className="utility-detail-label">Start of Tenancy Reading</div>
                <div className="utility-detail-value">
                  {formData[`${currentType}_meter_start_reading`] || 'Not set'}
                </div>
              </div>
              <div className="utility-detail-item">
                <div className="utility-detail-label">Latest Meter Reading</div>
                <div className="utility-detail-value">
                  {formData[`${currentType}_meter_latest_reading`] || 'Not set'}
                </div>
              </div>
            </div>
            <div className="utility-edit-actions">
              <Button 
                onClick={() => onOpenChange(false)} 
                className="btn-secondary"
                data-testid="utility-close-button"
              >
                Close
              </Button>
              <Button 
                onClick={() => setEditMode(true)} 
                className="btn-primary"
                data-testid="utility-edit-button"
              >
                <Edit3 size={18} />
                Edit {config.label}
              </Button>
            </div>
          </div>
        ) : (
          <div className="utility-detail-edit">
            <div className="form-group">
              <Label htmlFor={`${currentType}-serial`}>Serial Number</Label>
              <Input
                id={`${currentType}-serial`}
                value={formData[`${currentType}_meter`] || ''}
                onChange={(e) => setFormData({...formData, [`${currentType}_meter`]: e.target.value})}
                placeholder="Enter meter serial number"
                data-testid={`utility-${currentType}-serial-input`}
              />
            </div>
            <div className="form-group">
              <Label htmlFor={`${currentType}-supplier`}>Utility Company</Label>
              <Input
                id={`${currentType}-supplier`}
                value={formData[`${currentType}_supplier`] || ''}
                onChange={(e) => setFormData({...formData, [`${currentType}_supplier`]: e.target.value})}
                placeholder="Enter utility company name"
                data-testid={`utility-${currentType}-supplier-input`}
              />
            </div>
            <div className="form-group">
              <Label htmlFor={`${currentType}-start`}>Start of Tenancy Reading</Label>
              <Input
                id={`${currentType}-start`}
                value={formData[`${currentType}_meter_start_reading`] || ''}
                onChange={(e) => setFormData({...formData, [`${currentType}_meter_start_reading`]: e.target.value})}
                placeholder="Enter starting meter reading"
                data-testid={`utility-${currentType}-start-input`}
              />
            </div>
            <div className="form-group">
              <Label htmlFor={`${currentType}-latest`}>Latest Meter Reading</Label>
              <Input
                id={`${currentType}-latest`}
                value={formData[`${currentType}_meter_latest_reading`] || ''}
                onChange={(e) => setFormData({...formData, [`${currentType}_meter_latest_reading`]: e.target.value})}
                placeholder="Enter latest meter reading"
                data-testid={`utility-${currentType}-latest-input`}
              />
            </div>
            <div className="utility-edit-actions">
              <Button onClick={handleCancel} className="btn-secondary" data-testid="utility-cancel-button">
                Cancel
              </Button>
              <Button onClick={handleSave} className="btn-primary" data-testid="utility-save-button">
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
